import { Tabs } from "antd";
import ErrorBoundary from "components/error-boundary";
import FeatureHistory from "components/feature-history/feature-history";
import { FeatureTypes } from "entity-app/constants";
import { useContext, useMemo } from "react";
import {
  isPermissionNotGrantted,
  ProjectContext
} from "context/ProjectProvider";
import {
  CompanyParticipationType,
  ProjectPermissionEnum
} from "constants/index";
import FeatureCommentsTabComponent from "components/feature-activity-card/feature-comments-tab";
import { useCommentsCountLabel } from "components/feature-activity-card/use-comments-count-label";

function FeatureMaterialCommentsHistory(props: {
  featureInstanceData: any;
  tokenContents: any;
  isAttachementFeatureEnabled: boolean;
}) {
  const { featureInstanceData, tokenContents, isAttachementFeatureEnabled } =
    props;
  const { isProjectArchived } = useContext(ProjectContext);

  const style = useMemo(() => {
    if (featureInstanceData?._custom_fields) {
      return "max-h-[567px]";
    }
    return "max-h-[1300px]";
  }, [featureInstanceData?._custom_fields]);

  const permissionsForComments = useMemo(() => {
    const permissions = [];

    const canCreateComment = !isPermissionNotGrantted(
      ProjectPermissionEnum.CreateMaterialComment,
      tokenContents?.role ?? ""
    );

    const assigneeCompany =
      featureInstanceData?.feature_company_participants?.find(
        (e: any) =>
          e.company_participation_type_id ===
          CompanyParticipationType.RESPONSIBLE_CONTRACTOR
      );

    const canCreateMaterialCommentIfRC =
      !isProjectArchived &&
      assigneeCompany?.vendor_id ===
        tokenContents?.claims["x-hasura-vendor-id"];

    if (canCreateComment || canCreateMaterialCommentIfRC) {
      permissions.push("create_comment");
    }

    return permissions;
  }, [
    featureInstanceData,
    tokenContents?.claims,
    tokenContents?.role,
    isProjectArchived
  ]);

  const { label: commentsLabel, onCommentsCountChange } = useCommentsCountLabel(
    featureInstanceData?.id
  );

  const tabItems = [
    {
      label: commentsLabel,
      key: "1",
      children: (
        <ErrorBoundary>
          <div
            className={`py-4 px-2 overflow-y-auto overflow-x-hidden ${style}`}
            data-testid="fmch-history-tab-content"
          >
            <FeatureCommentsTabComponent
              permissions={permissionsForComments}
              featureKey="feature_instance_id"
              featureId={featureInstanceData?.id}
              isAttachementFeatureEnabled={isAttachementFeatureEnabled}
              auditFeatureType="MATERIALS"
              data-testid="fmch-comments-tab"
              onCommentsCountChange={onCommentsCountChange}
            />
          </div>
        </ErrorBoundary>
      )
    },
    {
      label: "History",
      key: "2",
      children: (
        <ErrorBoundary>
          <div
            className={`py-4 px-2 overflow-y-auto overflow-x-hidden ${style}`}
            data-testid="fmch-history-tab-content"
          >
            <FeatureHistory
              featureInstanceData={featureInstanceData}
              featureType={FeatureTypes.MATERIALS}
              data-testid="fmch-history-component"
            />
          </div>
        </ErrorBoundary>
      )
    }
  ];

  return (
    <div className="mt-3" data-testid="fmch-root">
      <Tabs items={tabItems} data-testid="fmch-tabs" />
    </div>
  );
}

export default FeatureMaterialCommentsHistory;
